import { Currency } from '@sequencehq/api'
import { format } from '@sequencehq/utils/dates'
import { v1ApiQuote } from 'modules/Cube/communication/external/quotes.api.v1/ports/entityLoaders/useLoadQuote'
import { QuoteMutationInterface } from 'modules/Cube/communication/external/quotes.api.v1/ports/entitySaving/useSaveQuote'
import { NEW_PRODUCT_PATTERN } from 'modules/Cube/domain/cube.constants'
import { CubeDomainInterface } from 'modules/Cube/domain/cube.domain'
import {
  CubeReducerState,
  CubeStatus,
  Product,
  QuoteContact
} from 'modules/Cube/domain/cube.domain.types'
import { quoteStatusAdapter } from 'modules/Cube/utils/status.adapter'
import { phasesToQuotePhases } from '../../common.api.v1/adapters/common.adapters.out'
import { calculateQuoteMetadata } from '@sequencehq/utils'

const commonFieldsToQuoteRequest = (
  data: CubeReducerState['data']['common']
): Pick<
  v1ApiQuote,
  | 'id'
  | 'title'
  | 'customerId'
  | 'currency'
  | 'quoteNumber'
  | 'status'
  | 'preliminaryBillingStartDate'
> => {
  return {
    id: data.id,
    title: data.title,
    customerId: data.customerId ? data.customerId : undefined,
    currency: data.currency as Currency,
    quoteNumber: data.alias,
    status: quoteStatusAdapter.out(data.status) as v1ApiQuote['status'],
    preliminaryBillingStartDate:
      data.startDate && format(data.startDate, 'yyyy-MM-dd')
  }
}

export const fieldsToQuoteRequest = (
  data: CubeReducerState['data']['quote']
): Pick<
  QuoteMutationInterface,
  | 'dealType'
  | 'expiryDays'
  | 'contactIds'
  | 'ccContactIds'
  | 'salesforceOpportunityId'
  | 'attachmentAssetIds'
  | 'counterSignerIds'
  | 'defaultDueDateDays'
  | 'amendment'
> => {
  const contactIds = Array.from(
    new Set(data.contacts.map(contact => contact.contactId))
  )

  return {
    dealType: data.dealType,
    expiryDays: data.expiresIn?.days,
    contactIds,
    ccContactIds: Array.from(
      new Set(
        data.ccContacts
          .map(contact => contact.contactId)
          .filter(contactId => !contactIds.includes(contactId))
      )
    ),
    salesforceOpportunityId: data.salesforceOpportunityId ?? null,
    attachmentAssetIds: data.attachmentAssets.map(asset => asset.id),
    counterSignerIds: Array.from(
      new Set(data.counterSigners.map(signer => signer.id))
    ),
    defaultDueDateDays: data.defaultDueDateDays,
    amendment: data.amendment
      ? {
          billingScheduleId: data.amendment.billingScheduleId,
          effectiveFrom: format(data.amendment.effectiveFrom, 'yyyy-MM-dd')
        }
      : undefined
  }
}

export const extractNewProducts = (
  products: CubeReducerState['data']['products']
): Product[] =>
  Object.values(products).filter(product =>
    product.id.match(NEW_PRODUCT_PATTERN)
  )

export const domainToApi = (
  data: CubeDomainInterface['queries']['rawData']['data']
): QuoteMutationInterface => {
  const phases = phasesToQuotePhases(data)

  return {
    ...commonFieldsToQuoteRequest(data.common),
    ...fieldsToQuoteRequest(data.quote),
    phases,
    presentation: applyMetadataToPresentation(data, phases),
    products: {
      new: extractNewProducts(data.products)
    }
  }
}

/**
 * Backfills the saved product order with any product missing from it, so
 * previews/PDFs stay stable. Shared with quoteTemplate.adapters.out.ts.
 */
export const getStablePriceEditorProductOrder = ({
  phases,
  productOrder
}: {
  phases: Array<{ prices: Array<{ productId?: string }> }>
  productOrder?: string[]
}) =>
  Array.from(
    new Set([
      ...(productOrder ?? []),
      ...phases.flatMap(phase =>
        phase.prices
          .map(price => price.productId)
          .filter((productId): productId is string => Boolean(productId))
      )
    ])
  )

export const applyMetadataToPresentation = (
  data: CubeDomainInterface['queries']['rawData']['data'],
  phases = phasesToQuotePhases(data)
) => {
  // Accepted quotes are locked: never recompute or overwrite presentation
  // metadata, including for quotes that pre-date the metadata fields (where
  // `data.presentation.metadata` is undefined). Pairs with
  // `toComparable` in QuoteEditorContent/utils/presentation.ts dropping metadata
  // from `hasPresentationChanged`, so we also don't flag accepted quotes as
  // having unsaved changes purely because the recomputed metadata would
  // differ.
  if (data.common.status === CubeStatus.QuoteAccepted) {
    return data.presentation
  }

  const metadata = calculateQuoteMetadata({
    startDate: data.common.startDate,
    phases,
    currency: data.common.currency ?? 'USD'
  })

  return {
    ...data.presentation,
    settings: {
      ...data.presentation.settings,
      priceEditorProductOrder: getStablePriceEditorProductOrder({
        phases,
        productOrder: data.presentation.settings?.priceEditorProductOrder
      })
    },
    metadata
  }
}

export const quoteContactToContactOption: (contact: QuoteContact) => {
  name: string
  email: string
} = contact => {
  return {
    name: contact.name ? contact.name : contact.email,
    email: contact.name ? contact.email : ''
  }
}
